// modelPicker.ts — the pure decisions behind the composer's model picker.
//
//   modelWidthCh      how wide the combobox is, in `ch`.
//   fleetDefault      the model a session with none pinned runs.
//   rememberedModel   whether a remembered new-session choice survives a
//                     fresh fleet list.
//   activeModelOf     the model the picker shows as in force.
//   settleModelDraft  what the box reverts to when a filter is abandoned.
//
// See useModelPicker.ts for the hook that wires these to state.

/** The narrowest the picker gets, in `ch`, while the fleet list loads. */
export const MODEL_MIN_WIDTH_CH = 16;

// The picker is sized to the longest id the fleet serves, not to the one
// currently selected. Two reasons it can't be the selection: the popover
// is pinned to the input's width, so a box that only fits the current
// model squeezes every longer name in the list; and a box that re-measures
// on every keystroke visibly jitters while you filter. Fleet ids are
// HuggingFace-style paths (`cyankiwi/Qwen3.6-27B-AWQ`), so the spread is
// wide and the difference is the whole id, not a few characters.
//
// In `ch` because that is what the primitive's calc(<n>ch + 3rem) speaks
// (DESIGN.md C-INPUT-5 — a pixel `width` would replace the calc and clip
// the value against the padding). The floor keeps the control a
// recognizable field while the fleet list is still in flight.
export function modelWidthCh(models: readonly string[], activeModel: string): number {
  return Math.max(
    MODEL_MIN_WIDTH_CH,
    ...models.map((m) => m.length),
    (activeModel || '').length,
  );
}

/** The fleet's default, or its first model when it names none. */
export function fleetDefault(models: readonly string[], def: string): string {
  return def || models[0] || '';
}

// Keep a remembered choice only while the fleet still serves it — a model
// that was undeployed since the last visit would otherwise stick around as
// a dead option and fail at provision time.
export function rememberedModel(cur: string, models: readonly string[], def: string): string {
  return cur && models.includes(cur) ? cur : fleetDefault(models, def);
}

// The model in force: the selected session's, or the pending choice for
// the next New. A row with no pinned model (created before the picker
// existed) shows the fleet default, which is what provisioning would hand
// it anyway.
export function activeModelOf(
  selected: { model: string } | null,
  defaultModel: string,
  newModel: string,
): string {
  return selected ? (selected.model || defaultModel) : newModel;
}

// What's typed in the box is not the model in force: it filters as you
// type, so mid-search it holds a fragment that matches nothing. On blur,
// an exact served id stays; anything else reverts so the box never claims
// a model the session isn't running.
export function settleModelDraft(cur: string, models: readonly string[], activeModel: string): string {
  return models.includes(cur) ? cur : activeModel;
}
